import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [costAlerts, setCostAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, convsRes, auditRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/conversations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/audit-logs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/cost-alerts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setConversations(convsRes.data);
      setAuditLogs(auditRes.data);
      setCostAlerts(alertsRes.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unlock`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User unlocked");
      fetchData();
    } catch (err) {
      toast.error("Failed to unlock user");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Change role to ${newRole}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Role updated to ${newRole}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change role");
    }
  };

  const handleTestAlert = async () => {
    try {
      const res = await axios.post(`${API}/admin/cost-alerts/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.message} (${res.data.current_count} msgs today)`);
    } catch (err) {
      toast.error("Failed to send test alert");
    }
  };

  const personalityNames = { guide: "Guide", friend: "Career Coach", mentor: "Mentor", coach: "Coach" };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50" data-testid="admin-page">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-extrabold text-slate-800">
              Your<span className="gradient-text">AImentor</span>
              <span className="ml-3 px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded-full font-bold">ADMIN</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="rounded-full" data-testid="admin-user-view-btn">
              User View
            </Button>
            <Button onClick={onLogout} variant="outline" className="rounded-full" data-testid="admin-logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-3xl font-extrabold mb-8">Admin Dashboard</h2>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard label="Total Users" value={stats.total_users} color="orange" testId="stat-users" />
              <StatCard label="Verified" value={stats.verified_users} color="teal" testId="stat-verified" />
              <StatCard label="Locked" value={stats.locked_users} color="red" testId="stat-locked" />
              <StatCard label="Conversations" value={stats.total_conversations} color="indigo" testId="stat-convs" />
              <StatCard label="Total Messages" value={stats.total_messages} color="pink" testId="stat-messages" />
              <StatCard label="Msgs (24h)" value={stats.messages_last_24h} color="yellow" testId="stat-msg24h" />
              <StatCard label="Msgs (7d)" value={stats.messages_last_7d} color="teal" testId="stat-msg7d" />
              <StatCard label="Assessments" value={stats.total_assessments} color="orange" testId="stat-assessments" />
            </div>
          )}

          {stats?.top_personalities && Object.keys(stats.top_personalities).length > 0 && (
            <Card className="p-6 mb-8" data-testid="personality-breakdown">
              <h3 className="text-lg font-bold mb-4">Conversations by Personality</h3>
              <div className="space-y-3">
                {Object.entries(stats.top_personalities).map(([key, count]) => {
                  const total = Object.values(stats.top_personalities).reduce((a, b) => a + b, 0);
                  const pct = total ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-semibold">{personalityNames[key] || key}</span>
                        <span className="text-slate-600">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-orange-500 to-pink-500" style={{ width: `${pct}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Tabs defaultValue="users" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
              <TabsTrigger value="conversations" data-testid="tab-convs">Conversations ({conversations.length})</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs ({auditLogs.length})</TabsTrigger>
              <TabsTrigger value="alerts" data-testid="tab-alerts">Cost Alerts</TabsTrigger>
            </TabsList>

            <TabsContent value="users">
              <Card className="overflow-hidden" data-testid="users-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Role</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Verified</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Messages</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Convs</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                          <td className="px-4 py-3 text-sm font-medium">{u.name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === "admin" ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-600"}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {u.email_verified ? (
                              <span className="text-green-600 font-bold">✓</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-semibold">{u.messages_sent}</td>
                          <td className="px-4 py-3 text-center text-sm">{u.conversation_count}</td>
                          <td className="px-4 py-3 text-center">
                            {u.is_locked ? (
                              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full font-bold">LOCKED</span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full font-bold">ACTIVE</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-2 justify-center flex-wrap">
                              {u.is_locked && (
                                <Button onClick={() => handleUnlockUser(u.id)} size="sm" variant="outline" className="rounded-full text-xs" data-testid={`unlock-user-${u.id}`}>
                                  Unlock
                                </Button>
                              )}
                              {u.id !== user.id && (
                                <Button
                                  onClick={() => handleRoleChange(u.id, u.role === "admin" ? "user" : "admin")}
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full text-xs"
                                  data-testid={`role-toggle-${u.id}`}
                                >
                                  {u.role === "admin" ? "Demote" : "Promote"}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="conversations">
              <Card className="overflow-hidden" data-testid="conversations-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Personality</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase">Messages</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Started</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {conversations.map((c) => (
                        <tr key={c.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium">{c.user_name}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{c.user_email}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded-full font-bold">
                              {personalityNames[c.personality] || c.personality}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-semibold">{c.message_count}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{new Date(c.updated_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card className="overflow-hidden" data-testid="audit-logs-table">
                <div className="p-4 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-bold text-lg">Admin Action History</h3>
                  <p className="text-sm text-slate-600">Track sensitive admin actions like user unlocks and role changes</p>
                </div>
                {auditLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No audit events yet. Actions will appear here when admins unlock users or change roles.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">When</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Admin</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Target</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                            <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">{log.admin_email}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                log.action === "unlock_user" ? "bg-green-100 text-green-600" :
                                log.action === "change_role" ? "bg-indigo-100 text-indigo-600" :
                                "bg-slate-100 text-slate-600"
                              }`}>
                                {log.action.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">{log.target_email || "—"}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {log.details && Object.keys(log.details).length > 0 ? (
                                <div className="text-xs">
                                  {log.details.old_role && log.details.new_role && (
                                    <span>{log.details.old_role} → <strong>{log.details.new_role}</strong></span>
                                  )}
                                  {log.details.target_name && !log.details.old_role && (
                                    <span>Name: {log.details.target_name}</span>
                                  )}
                                </div>
                              ) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="alerts">
              <Card className="p-6" data-testid="cost-alerts-panel">
                <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-lg">Daily AI Cost Alerts</h3>
                    <p className="text-sm text-slate-600">
                      Alert sent to admin when daily messages exceed <strong>{costAlerts?.threshold || 200}</strong> messages
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Status: <span className={costAlerts?.enabled ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                        {costAlerts?.enabled ? "ENABLED" : "DISABLED"}
                      </span>
                    </p>
                  </div>
                  <Button onClick={handleTestAlert} className="btn-primary text-sm" data-testid="send-test-alert-btn">
                    Send Test Alert
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <p className="text-xs uppercase font-bold text-orange-600 mb-1">Last 24h Messages</p>
                    <p className="text-2xl font-extrabold text-orange-700">{stats?.messages_last_24h || 0}</p>
                  </div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-xs uppercase font-bold text-red-600 mb-1">Alert Threshold</p>
                    <p className="text-2xl font-extrabold text-red-700">{costAlerts?.threshold || 200}</p>
                  </div>
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                    <p className="text-xs uppercase font-bold text-indigo-600 mb-1">Alerts Sent</p>
                    <p className="text-2xl font-extrabold text-indigo-700">{costAlerts?.history?.length || 0}</p>
                  </div>
                </div>

                <h4 className="font-bold text-sm text-slate-700 mb-3">Alert History</h4>
                {(!costAlerts?.history || costAlerts.history.length === 0) ? (
                  <div className="p-6 text-center text-slate-500 border border-dashed border-slate-200 rounded-xl">
                    No alerts sent yet. Alerts fire once per day when your daily message count crosses the threshold.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {costAlerts.history.map((alert) => (
                      <div key={alert.id} className="p-4 bg-slate-50 rounded-xl flex justify-between items-center" data-testid={`cost-alert-${alert.id}`}>
                        <div>
                          <p className="font-bold">{alert.date}</p>
                          <p className="text-sm text-slate-600">{alert.message_count} messages sent (threshold: {alert.threshold})</p>
                        </div>
                        <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                          ⚠️ ALERT SENT
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}

function StatCard({ label, value, color, testId }) {
  const colorMap = {
    orange: "from-orange-500 to-orange-600",
    teal: "from-teal-500 to-teal-600",
    red: "from-red-500 to-red-600",
    indigo: "from-indigo-500 to-indigo-600",
    pink: "from-pink-500 to-pink-600",
    yellow: "from-yellow-400 to-yellow-500",
  };
  return (
    <Card className={`p-6 bg-gradient-to-br ${colorMap[color]} text-white border-0`} data-testid={testId}>
      <p className="text-xs uppercase font-bold opacity-90 mb-1">{label}</p>
      <p className="text-3xl font-extrabold">{value}</p>
    </Card>
  );
}
